import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { notify } from "@organisms/notify";
import { useUserStore } from "@stores/userStore";

import type { IApiError, IApiErrorObject } from "@api";

export const useErrorHandler = (
  onThrow: (message: string, status: number) => void,
) => {
  const { t } = useTranslation("error");

  const onError = useCallback(
    (err: string, errors?: IApiErrorObject[]) => {
      if (errors && Array.isArray(errors) && errors?.length > 0) {
        for (const error of errors) {
          if (!toast.isActive(error?.message || t("apiError.error"))) {
            notify.error({
              title: t("apiError.error"),
              description: error?.message,
              toastId: error?.message || t("apiError.error"),
            });
          }
        }
      } else if (!toast.isActive(err)) {
        notify.error({
          title: t("apiError.error"),
          description: err,
          toastId: err,
        });
      }
    },
    [t],
  );

  return useCallback(
    async (error: IApiError) => {
      if (!error.response) {
        if (error.code !== "ERR_CANCELED") {
          onError(t("apiError.unable_server_response"));
        }
        return Promise.reject(error);
      }
      if (
        globalThis.window?.env?.isElectron &&
        error.response.status === 503 &&
        Array.isArray(error.response.data) &&
        error.response.data.some((item) => item.code === "offline_unavailable")
      ) {
        // Keep connectivity errors in the affected query instead of throwing
        // the whole app into its route error boundary. Background reads stay quiet;
        // an explicitly attempted write still reports why it could not complete.
        if (
          !["get", "head"].includes(
            error.config?.method?.toLowerCase() ?? "get",
          )
        )
          onError(t("apiError.503"), error.response.data);
        return Promise.reject(error);
      }
      switch (error.response.status) {
        case 401:
          useUserStore.getState().logout();
          onError(t("apiError.401"), error?.response?.data);
          break;

        case 403:
          onError(t("apiError.403"), error?.response?.data);
          onThrow(t("apiError.403"), 403);
          break;
        case 500:
          onError(t("apiError.500"), error?.response?.data);
          onThrow(t("apiError.500"), 500);
          break;
        case 400:
          onError(t("apiError.400"), error?.response?.data);
          break;
        case 503:
          onError(t("apiError.503"), error?.response?.data);
          onThrow(t("apiError.503"), 503);
          break;
        case 404:
          onError(t("apiError.404"), error?.response?.data);
          onThrow(t("apiError.404"), 404);
          break;

        case 406:
          onError(t("apiError.error"), error?.response?.data);
          break;

        case 422:
          onError(t("apiError.error"), error?.response?.data);
          break;

        default:
          onError(t("apiError.error"), error?.response?.data);
      }

      return Promise.reject(error);
    },
    [onError, onThrow, t],
  );
};
